import React from "react";
import { TkDateDialogProps } from "../../../type/componentsType/TkDateDialogProps";
import TkDatePicker from "../../atoms/TkDatePicker";
import TkDialog from "../../atoms/TkDialog";
import dayjs from "dayjs";
import TkPropertyMessageGet from "../../../utils/TkPropertyMessageGet";

/**
 * TkDateDialog部品は、日付選択ダイアログを表示するための部品です。
 * @param open ダイアログの表示状態
 * @param onClose ダイアログの外側をクリックしたときのハンドラー関数
 * @param onCancel キャンセルボタンをクリックしたときのハンドラー関数
 * @param onOky OKボタンをクリックしたときのハンドラー関数
 * @param title ダイアログのタイトル
 * @param contextText ダイアログの説明文
 * @param cancelText キャンセルボタンのテキスト
 * @param okText OKボタンのテキスト
 * @param label 日付選択のラベル
 * @param wantToSetValue 日付選択の初期値
 * @returns TkDateDialogコンポーネント
 */
const TkDateDialog: React.FC<TkDateDialogProps> = (props) => {
  const {
    open = false,
    onClose,
    onCancel,
    onOky,
    children,
    title,
    contextText,
    cancelText,
    okText,
    label,
    wantToSetValue,
    requeried,
    minDate,
    maxDate,
    ...rest
  } = props;

  const [value, setValue] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string>("");

  /**
   * 日付選択が変更された時のハンドラー関数です。
   * @param value 選択された日付
   */
  const handleOnChange = (value: any) => {
    setValue(value.target.value);

    if (!value.target.value) {
      setError(TkPropertyMessageGet("TKMSG_SCR0043E"));
      return;
    } else if (minDate && dayjs(value.target.value).isBefore(dayjs(minDate))) {
      setError(
        TkPropertyMessageGet(
          "TKMSG_SCR0050E",
          dayjs(minDate).format("YYYY / MM / DD")
        )
      );
      return;
    } else if (maxDate && dayjs(value.target.value).isAfter(dayjs(maxDate))) {
      setError(
        TkPropertyMessageGet(
          "TKMSG_SCR0051E",
          dayjs(maxDate).format("YYYY / MM / DD")
        )
      );
      return;
    }

    setError("");
  };

  /**
   * OKボタンがクリックされた時のハンドラー関数です。
   */
  const handleOky = () => {
    if (!value) {
      setError(TkPropertyMessageGet("TKMSG_SCR0043E"));
      return;
    } else if (minDate && dayjs(value).isBefore(dayjs(minDate))) {
      setError(
        TkPropertyMessageGet(
          "TKMSG_SCR0050E",
          dayjs(minDate).format("YYYY / MM / DD")
        )
      );
      return;
    } else if (maxDate && dayjs(value).isAfter(dayjs(maxDate))) {
      setError(
        TkPropertyMessageGet(
          "TKMSG_SCR0051E",
          dayjs(maxDate).format("YYYY / MM / DD")
        )
      );
      return;
    } else if (!dayjs(value, "YYYY/MM/DD", true).isValid()) {
      return;
    }

    setError("");
    onOky && onOky(value);
  };

  const handleOnCancel = () => {
    setValue(null);
    setError("");
    onCancel && onCancel();
  };

  const handleOnClose = () => {
    setValue(null);
    setError("");
    onClose && onClose();
  };

  return (
    <TkDialog
      {...rest}
      title={title}
      open={open}
      onClose={handleOnClose}
      contextText={contextText}
      onSave={handleOky}
      onCancel={handleOnCancel}
      cancelText={cancelText}
      okText={okText}
    >
      <TkDatePicker
        wantToSetValue={wantToSetValue}
        label={label}
        onChange={handleOnChange}
        minDate={minDate}
        maxDate={maxDate}
        error={!!error}
        errorMessage={error}
      />
    </TkDialog>
  );
};

export default TkDateDialog;
